import { tenantIcon } from './tenantIcons'
import type { RuntimeIconProps } from './types'

type IconComponent = React.ComponentType<RuntimeIconProps>
type CompiledIconLookup = (name: string) => IconComponent | undefined

let compiledLookup: CompiledIconLookup = () => undefined

/**
 * Registers the compiled product registry as the fallback, keeping this module — and so the
 * package barrel — free of any path into `../compiled`, whose 374 SVG modules must stay off every
 * host's eager chunk (see `../compiled/index.ts`).
 *
 * Read lazily on each lookup, so registering at app module scope carries no ordering hazard, and
 * an unregistered runtime degrades to tenant artwork only rather than throwing.
 */
export const setCompiledIconSource = (next: CompiledIconLookup) => {
    compiledLookup = next
}

/**
 * Resolves an icon name the way every rendering surface should: the tenant's own artwork if its
 * config supplies that name, otherwise the compiled product glyph.
 *
 * Lives beside `tenantIcon` rather than next to the compiled registry so that a Module Federation
 * remote reaches it through the shared barrel. `aos-ui-universal` is shared on the exact request
 * only (`apps/shared/shared-dependencies.js`), so a subpath import is compiled per remote — and
 * composing tenant lookup from there would give each remote its own unregistered copy of this
 * module's state, which is the trap `configureIcons` fell into. Import it from the barrel.
 *
 * `compiledName` exists for the restriction surfaces, where the config name and the compiled name
 * differ: the tenant keys artwork by the name it wrote in config, but no compiled glyph carries
 * that restriction-specific name, so the fallback needs the remapped one.
 */
export const iconFor = (name: string | undefined, compiledName: string | undefined = name) =>
    tenantIcon(name) ?? (compiledName ? compiledLookup(compiledName) : undefined)
